import React, { Suspense } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import { mapsLink } from '../utils/puntiRitiro.generated';

const PuntoMapPreview = React.lazy(() => import('./PuntoMapPreview'));

const BLU = '#2563EB';
const fmt = (n) => `€${Number(n || 0).toFixed(2)}`;

/**
 * Blocco "Ritiro e riconsegna" del modal di prenotazione.
 *
 * Mostra due selezioni indipendenti (sede preimpostata), il supplemento
 * accanto a ogni opzione, l'indirizzo, le note e una piccola mappa del punto
 * scelto. Il prezzo mostrato qui è solo un'anteprima: l'importo addebitato è
 * sempre ricalcolato dal server (createPaymentIntent).
 *
 * Props:
 *  - sede:   punto virtuale della sede (buildSedePoint)
 *  - punti:  punti aggiuntivi ATTIVI (getActivePunti)
 *  - ritiroId / riconsegnaId: id selezionati ('sede' di default)
 *  - onChange({ ritiroId?, riconsegnaId? })
 *  - disabled: blocca le select (es. durante il pagamento)
 */
const PuntiRitiroPicker = ({ sede, punti, ritiroId, riconsegnaId, onChange, disabled = false }) => {
  const all = [sede, ...punti];
  const byId = (id) => all.find((p) => p.id === id) || sede;

  const renderSide = (kind) => {
    const isRitiro = kind === 'ritiro';
    const selectedId = isRitiro ? ritiroId : riconsegnaId;
    const selected = byId(selectedId);
    const supplemento = isRitiro ? selected.supplementoRitiro : selected.supplementoRiconsegna;
    const link = mapsLink(selected);
    const selectId = `punto-${kind}`;

    return (
      <div className="bg-white rounded-xl p-3 border border-slate-200">
        <label htmlFor={selectId} className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5">
          {isRitiro ? 'Dove ritiri' : 'Dove riconsegni'}
        </label>
        <select
          id={selectId}
          value={selectedId}
          disabled={disabled}
          onChange={(e) => onChange(isRitiro ? { ritiroId: e.target.value } : { riconsegnaId: e.target.value })}
          className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-white focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600"
        >
          {all.map((p) => {
            const s = isRitiro ? p.supplementoRitiro : p.supplementoRiconsegna;
            return (
              <option key={p.id} value={p.id}>
                {p.nome}{p.isSede ? ' (incluso)' : ''}{s > 0 ? ` — +${fmt(s)}` : ''}
              </option>
            );
          })}
        </select>

        <div className="mt-2 text-xs text-slate-600 space-y-1">
          {selected.indirizzo && (
            <p className="flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-400" /><span>{selected.indirizzo}</span></p>
          )}
          {selected.note && <p className="italic text-slate-500">{selected.note}</p>}
          <p className="font-semibold" style={{ color: supplemento > 0 ? BLU : '#059669' }}>
            {supplemento > 0 ? `Supplemento: ${fmt(supplemento)}` : 'Nessun supplemento'}
          </p>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold hover:underline" style={{ color: BLU }}>
              Apri in Google Maps <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>

        {Number.isFinite(selected.lat) && Number.isFinite(selected.lng) && (
          <div className="mt-2">
            <Suspense fallback={<div className="h-32 rounded-xl bg-slate-100 animate-pulse" />}>
              <PuntoMapPreview lat={selected.lat} lng={selected.lng} nome={selected.nome} />
            </Suspense>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-2xl" data-testid="punti-ritiro-picker">
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4" style={{ color: BLU }} />
        <span className="font-bold text-slate-800 text-sm">Ritiro e riconsegna</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {renderSide('ritiro')}
        {renderSide('riconsegna')}
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Ritiro e riconsegna sono indipendenti. I supplementi si sommano al totale e non sono scontabili con i codici sconto.
      </p>
    </div>
  );
};

export default PuntiRitiroPicker;
